import type { CaseStudyMetric } from "@/types/marketing"

type WorkIndexCopy = {
  hero: {
    eyebrow: string
    heading: string
    body: string
  }
  vancleef: {
    relationship: string
    audience: string
    heading: string
    problem: string
    outcome: string
    systems: string[]
    metrics: CaseStudyMetric[]
    link: string
  }
  cajanka: {
    relationship: string
    audience: string
    heading: string
    problem: string
    outcome: string
    systems: string[]
    evidence: string
    link: string
  }
  final: {
    eyebrow: string
    heading: string
    body: string
    action: string
  }
}

export const WORK_INDEX_COPY: Record<"hr" | "en", WorkIndexCopy> = {
  hr: {
    hero: {
      eyebrow: "Rad u praksi",
      heading: "Sustavi koji povezuju iskustvo kupca i stvarni posao iza njega.",
      body: "Dva različita poslovna konteksta. U oba slučaja počeli smo od ponavljajućeg problema, povezali potrebne informacije i ostavili ljudima kontrolu nad važnim odlukama.",
    },
    vancleef: {
      relationship: "VanCleef · vanjski klijent",
      audience: "Računovodstveni timovi koji vode turističke klijente",
      heading: "Četiri odvojena turistička sustava, jedan kontrolirani računovodstveni proces.",
      problem: "Računovodstveni timovi ručno su usklađivali podatke iz eVisitora, Booking.com izvoza, Novasola i e-računa.",
      outcome: "Privatna operativna platforma povezuje uvoze, provjere, izračune i pripremu računa uz vidljivu ljudsku kontrolu.",
      systems: ["eVisitor", "Booking.com uvoz", "Novasol", "e-računi"],
      metrics: [
        { kind: "measured", value: "4", label: "povezana sustava" },
        { kind: "capacity", value: "300", label: "autentikacija po TAN unosu" },
        { kind: "estimate", value: "67–125 h", label: "na 500 računa", basis: "Procjena iz koda" },
      ],
      link: "Pogledajte studiju slučaja",
    },
    cajanka: {
      relationship: "Čajanka · poslovanje u vlasništvu Zmile d.o.o.",
      audience: "Vlasnički vođeno ecommerce i maloprodajno poslovanje",
      heading: "Web-trgovina povezana s računovodstvom, zalihama, naručivanjem i partnerima.",
      problem: "Rast trgovine stvara administraciju koja lako ostane odvojena od iskustva kupca.",
      outcome: "Povezani tokovi drže prodaju, dokumente, zalihe i partnerski program u istom operativnom kontekstu.",
      systems: ["Web-trgovina", "Računovodstvo", "Zalihe", "Partneri"],
      evidence: "Vlastito poslovanje · izračuni i razina dokaza vidljivi su u studiji slučaja.",
      link: "Pogledajte studiju slučaja",
    },
    final: {
      eyebrow: "Vaš početni problem ne mora biti velik",
      heading: "Pronađimo korisnu početnu točku.",
      body: "U besplatnom 30-minutnom razgovoru prolazimo kroz ograničenje, postojeće alate i rezultat koji bi imao smisla.",
      action: "Zatražite besplatne 30-minutne konzultacije",
    },
  },
  en: {
    hero: {
      eyebrow: "Work in practice",
      heading: "Systems connecting the customer experience to the real work behind it.",
      body: "Two different operating contexts. In both, we started with a repeated problem, connected the necessary information, and kept people in control of important decisions.",
    },
    vancleef: {
      relationship: "VanCleef · external client",
      audience: "Accounting teams managing tourism clients",
      heading: "Four disconnected tourism systems, one controlled accounting workflow.",
      problem: "Accounting teams manually reconciled data from eVisitor, Booking.com exports, Novasol, and e-računi.",
      outcome: "A private operations platform connects imports, reviews, calculations, and invoice preparation with visible human control.",
      systems: ["eVisitor", "Booking.com import", "Novasol", "e-računi"],
      metrics: [
        { kind: "measured", value: "4", label: "systems connected" },
        { kind: "capacity", value: "300", label: "authentications per TAN upload" },
        { kind: "estimate", value: "67–125 h", label: "per 500 invoices", basis: "Code-derived estimate" },
      ],
      link: "Read the case study",
    },
    cajanka: {
      relationship: "Čajanka · a business owned by Zmile d.o.o.",
      audience: "Founder-led ecommerce and retail operations",
      heading: "An ecommerce experience connected to accounting, stock, ordering, and partners.",
      problem: "Retail growth creates administration that can easily become detached from the customer experience.",
      outcome: "Connected workflows keep sales, documents, stock, and the affiliate program in the same operational context.",
      systems: ["Webshop", "Accounting", "Stock", "Partners"],
      evidence: "Owned business · calculations and evidence levels are visible in the full case study.",
      link: "Read the case study",
    },
    final: {
      eyebrow: "Your starting problem does not need to be large",
      heading: "Let’s find a useful starting point.",
      body: "In a free 30-minute conversation, we look at the constraint, the tools already in place, and the outcome worth pursuing.",
      action: "Request a free 30-minute consultation",
    },
  },
}
